// Guessing the buyer's currency from the browser.
//
// The IANA time zone is used as the primary signal: it's a better proxy for where
// someone actually is than the UI language (plenty of en-US browsers outside the US),
// it needs no network request, and no data leaves the browser. The language region is
// used as a fallback for the (rare) case where the time zone isn't recognised.
//
// This is only a guess for the initial display currency. An explicit pick from the
// currency switcher always wins, and unknown locations fall back to the marketplace
// currency. See the currency context for the precedence order.

// Countries whose currency the marketplace can display. Countries that use a currency
// outside of appSettings.displayCurrencies are intentionally absent: those fall back to
// the marketplace currency.
const COUNTRY_CURRENCIES = {
  AU: 'AUD',
  US: 'USD',
  GB: 'GBP',
  NZ: 'NZD',
  CA: 'CAD',
  SG: 'SGD',
  JP: 'JPY',
  IN: 'INR',
  CH: 'CHF',
  // Eurozone
  AD: 'EUR',
  AT: 'EUR',
  BE: 'EUR',
  CY: 'EUR',
  DE: 'EUR',
  EE: 'EUR',
  ES: 'EUR',
  FI: 'EUR',
  FR: 'EUR',
  GR: 'EUR',
  HR: 'EUR',
  IE: 'EUR',
  IT: 'EUR',
  LT: 'EUR',
  LU: 'EUR',
  LV: 'EUR',
  MC: 'EUR',
  ME: 'EUR',
  MT: 'EUR',
  NL: 'EUR',
  PT: 'EUR',
  SI: 'EUR',
  SK: 'EUR',
  SM: 'EUR',
  VA: 'EUR',
};

// IANA time zone -> ISO 3166-1 alpha-2 country. Only zones that resolve to a currency
// in COUNTRY_CURRENCIES are listed; anything else falls back to the marketplace currency.
const TIMEZONE_COUNTRIES = {
  // United States
  'America/New_York': 'US',
  'America/Detroit': 'US',
  'America/Chicago': 'US',
  'America/Menominee': 'US',
  'America/Indiana/Indianapolis': 'US',
  'America/Kentucky/Louisville': 'US',
  'America/North_Dakota/Center': 'US',
  'America/Denver': 'US',
  'America/Boise': 'US',
  'America/Phoenix': 'US',
  'America/Los_Angeles': 'US',
  'America/Anchorage': 'US',
  'America/Juneau': 'US',
  'America/Sitka': 'US',
  'America/Nome': 'US',
  'America/Adak': 'US',
  'Pacific/Honolulu': 'US',
  // Canada
  'America/Toronto': 'CA',
  'America/Montreal': 'CA',
  'America/Winnipeg': 'CA',
  'America/Regina': 'CA',
  'America/Edmonton': 'CA',
  'America/Vancouver': 'CA',
  'America/Halifax': 'CA',
  'America/Moncton': 'CA',
  'America/Glace_Bay': 'CA',
  'America/Goose_Bay': 'CA',
  'America/St_Johns': 'CA',
  'America/Whitehorse': 'CA',
  'America/Yellowknife': 'CA',
  'America/Iqaluit': 'CA',
  // United Kingdom
  'Europe/London': 'GB',
  'Europe/Belfast': 'GB',
  'Europe/Guernsey': 'GB',
  'Europe/Jersey': 'GB',
  'Europe/Isle_of_Man': 'GB',
  // New Zealand
  'Pacific/Auckland': 'NZ',
  'Pacific/Chatham': 'NZ',
  // Switzerland
  'Europe/Zurich': 'CH',
  // Singapore, Japan, India
  'Asia/Singapore': 'SG',
  'Asia/Tokyo': 'JP',
  'Asia/Kolkata': 'IN',
  'Asia/Calcutta': 'IN',
  // Eurozone
  'Europe/Andorra': 'AD',
  'Europe/Vienna': 'AT',
  'Europe/Brussels': 'BE',
  'Europe/Nicosia': 'CY',
  'Asia/Nicosia': 'CY',
  'Europe/Berlin': 'DE',
  'Europe/Busingen': 'DE',
  'Europe/Tallinn': 'EE',
  'Europe/Madrid': 'ES',
  'Africa/Ceuta': 'ES',
  'Atlantic/Canary': 'ES',
  'Europe/Helsinki': 'FI',
  'Europe/Paris': 'FR',
  'Europe/Athens': 'GR',
  'Europe/Zagreb': 'HR',
  'Europe/Dublin': 'IE',
  'Europe/Rome': 'IT',
  'Europe/Vilnius': 'LT',
  'Europe/Luxembourg': 'LU',
  'Europe/Riga': 'LV',
  'Europe/Monaco': 'MC',
  'Europe/Podgorica': 'ME',
  'Europe/Malta': 'MT',
  'Europe/Amsterdam': 'NL',
  'Europe/Lisbon': 'PT',
  'Atlantic/Madeira': 'PT',
  'Atlantic/Azores': 'PT',
  'Europe/Ljubljana': 'SI',
  'Europe/Bratislava': 'SK',
  'Europe/San_Marino': 'SM',
  'Europe/Vatican': 'VA',
};

// Australia has a dozen zones that all resolve to the same country.
const TIMEZONE_COUNTRY_PREFIXES = [{ prefix: 'Australia/', country: 'AU' }];

const getCountryFromTimeZone = () => {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!timeZone) {
      return null;
    }
    const prefixMatch = TIMEZONE_COUNTRY_PREFIXES.find(entry => timeZone.startsWith(entry.prefix));
    return TIMEZONE_COUNTRIES[timeZone] || prefixMatch?.country || null;
  } catch (e) {
    return null;
  }
};

const getCountryFromLanguage = () => {
  try {
    const languages = navigator.languages?.length > 0 ? navigator.languages : [navigator.language];
    for (const language of languages) {
      if (!language) {
        continue;
      }
      // e.g. "en-AU" -> "AU". Intl.Locale also handles tags like "zh-Hans-SG".
      const region = new Intl.Locale(language).region;
      if (region && COUNTRY_CURRENCIES[region]) {
        return region;
      }
    }
    return null;
  } catch (e) {
    return null;
  }
};

/**
 * Guess the currency to show prices in, based on the browser's time zone (and the
 * language region as a fallback).
 *
 * @param {Array<string>} supportedCurrencies - currencies the marketplace can display
 *
 * @return {string|null} currency code, or null if the location is unknown or its
 * currency is not supported. The caller decides the fallback.
 */
export const detectCurrencyFromBrowser = supportedCurrencies => {
  if (typeof window === 'undefined' || typeof Intl === 'undefined') {
    return null;
  }

  const country = getCountryFromTimeZone() || getCountryFromLanguage();
  const currency = country ? COUNTRY_CURRENCIES[country] : null;

  return currency && supportedCurrencies?.includes(currency) ? currency : null;
};
